import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useConfirm } from "components/hooks/useConfirm";
import { useEffect, useState } from "react";
import { Card } from "@cofano/cck";
import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import { Fields, getMultiValuesField, hasOptionalFieldPermission } from "components/utils/Permission";
import { intl } from "LocaleWrapper";
import AgreementForm from "view/agreements/AgreementForm";
import UpdateAgreementOrderDialog from "view/contractsNew/components/dialogs/UpdateAgreementOrderDialog";
import AgreementTemplatesDialog from "view/contractsNew/components/dialogs/AgreementTemplatesDialog";
import {
	convertAgreementToForm,
	filterServiceAgreements,
	getDefaultInvoiceGroupType,
	hasSameAgreementOrder,
	sortAgreements,
	stripTemplateIds,
} from "view/agreements/utils/AgreementUtils";
import { CalculationMethodType, AgreementTypeEnum, CharterType, CharterSubtype } from "__generated__/enums";
import { useQuery } from "@apollo/client";
import { getAgreementListMasterData_query } from "view/agreements/utils/AgreementQueries";
import { Box, Tooltip, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import type { AgreementType, CargoLinkedCharter } from "types/index";
import { useStoreState, Session } from "ezStore";
import AddAgreementButton from "view/agreements/components/button/AddAgreementButton";
import { AgreementSchema } from "view/agreements/schema";
import { AgreementFormInput } from "view/agreements/validation/agreement.zod";
import InvoiceGroupTypeSelect from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

export type UpdateAgreement = (index: number, value: AgreementFormInput) => void;

type FormValues = {
	id?: number;
	isParentContract?: boolean;
	parentContract?: object;
	isChildContract?: boolean;
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	agreements: AgreementFormInput[];
}

type AgreementCreateEditProps = {
	currency: CurrencyFragment;
	session: Session;
	charter?: CargoLinkedCharter | PurchaseCharterFromQuery;
}

const AgreementCreateEdit = ({ charter, currency, session }: AgreementCreateEditProps) => {
	const favorites = useStoreState(store => store.session.user?.properties.favorites);

	const { control, formState: { errors } } = useFormContext<FormValues>();

	const confirm = useConfirm();

	const { data: masterData } = useQuery(getAgreementListMasterData_query);
	const agreementTypes = masterData?.agreementTypes as AgreementType[] ?? [];

	const [ agreementTemplatesDialogOpen, openAgreementTemplatesDialog ] = useState<boolean>(false);
	const [ agreementOrderDialogOpen, openAgreementOrderDialog ] = useState<boolean>(false);

	const contractId = useWatch({ control, name: "id" });
	const isParentContract = useWatch({ control, name: "isParentContract" });
	const isChildContract = useWatch({ control, name: "parentContract" }) ? true : false;
	const charterType = useWatch({ control, name: "charterType" });
	const charterSubtype = useWatch({ control, name: "charterSubtype" });
	const agreements = useWatch({ control, name: "agreements" }) ?? [];

	const customAgreementTypesFromPlugin = getMultiValuesField(session, "customAgreementTypes").trim().length > 0 ?
		getMultiValuesField(session, "customAgreementTypes").split(";") as AgreementTypeEnum[] : undefined;

	const cargos = charter && charter.type === CharterType.Sales && "cargos" in charter ? charter?.cargos : undefined;
	const customAgreementTypes = filterServiceAgreements(customAgreementTypesFromPlugin, charterType, charterSubtype, cargos);

	const { fields, remove, update, replace, append } = useFieldArray({
		control,
		keyName: "generatedId",
		name: "agreements",
	});

	const updateAgreement: UpdateAgreement = (index, value) => {
		const current = agreements?.[index];

		update(index, {
			...current,
			...value,
			uuid: current?.uuid ?? value.uuid,
			id: current?.id ?? value.id,
		});
	};

	const createDefaultAgreement = () => ({
		uuid: crypto.randomUUID(),
		id: undefined,
		type: undefined,
		priority: fields.length + 1,
		description: "",
		costType: undefined,
		calculationMethod: null,
		isDefault: false,
		invoiceTo: undefined,
		invoiceGroupType: undefined,
	});

	const canDelete = (agreement?: Pick<AgreementFormInput, "id" | "uuid">) => {
		if (!agreement) {
			return true;
		}

		const referencedByCommission = (agreements ?? [])
			.filter(x => x?.calculationMethod?.type === CalculationMethodType.PercentageOnAgreements)
			.flatMap(x => x?.calculationMethod?.applicableAgreements ?? []);

		if (referencedByCommission.length === 0) {
			return true;
		}

		return !referencedByCommission.some(x =>
			(x?.id != null && x.id === agreement.id) ||
			(x?.uuid && x.uuid === agreement.uuid),
		);
	};

	const getDefaultCostType = (agreementType?: AgreementTypeEnum) => {
		return agreementTypes?.find(x => x?.type === agreementType)?.costType ?? undefined;
	};

	const getTitle = (index: number) => {
		const currentAgreement = agreements[index];

		return <Typography
			textTransform="none"
			component="span"
			fontWeight="bold">
			{currentAgreement?.summary ?? intl.formatMessage({ id: `AGREEMENT_TYPE_${currentAgreement?.type}` })}
		</Typography>;
	};

	const getSubtitle = (index: number) => {
		const currentAgreement = agreements[index];

		return currentAgreement?.conditionsSummary ? <Typography
			textTransform="none"
			component="span"
			fontSize={13}
		>
			{currentAgreement.conditionsSummary}
		</Typography> : null;
	};

	const onAppend = (type: AgreementTypeEnum) => {
		const defaultAgreement = createDefaultAgreement();
		const schemaPerType = type ? AgreementSchema[type] : undefined;
		const typeDefaults = schemaPerType?.defaultValues ?? {};

		append({
			...defaultAgreement,
			...typeDefaults,
			type: type,
			costType: getDefaultCostType(type),
			invoiceGroupType: getDefaultInvoiceGroupType(agreementTypes, type, charterType),
		});
	};

	const onRemove = (index: number) => {
		const agreement = agreements?.[index];
		if (!canDelete(agreement)) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "CANNOT_DELETE_AGREEMENT" }),
				content: <Typography color={"textPrimary"}>
					{intl.formatMessage({ id: "AGREEMENT_REFERENCED_BY_APPLICABLE_AGREEMENTS" })}
				</Typography>,
				confirmDisabled: true,
			});
			return;
		}
		confirm({ type: "DELETE" }).then(() => remove(index));
	};

	const onDuplicate = (index: number) => {
		const agreement = agreements?.[index];

		append({
			...agreement,
			id: undefined,
			uuid: crypto.randomUUID(),
			calculationMethod: agreement.calculationMethod ? {
				...agreement.calculationMethod,
				id: undefined,
			} : null,
		});
	};

	const getColor = (index: number, agreement: AgreementFormInput) => {
		if (errors?.agreements?.[index] || agreement.isDefault) {
			return "red";
		}
	};

	useEffect(() => {
		const sortedAgreements = sortAgreements(favorites ?? [], agreementTypes, agreements);
		if (hasSameAgreementOrder(sortedAgreements, agreements)) {
			return;
		}
		replace(sortedAgreements);
	}, [ agreements, favorites, agreementTypes ]);

	return (<Card
		disablePadding
		title={(!hasOptionalFieldPermission(session, Fields.CONTRACT, "contractOptions") || !contractId)
		&& intl.formatMessage({ id: "AGREEMENTS" })}
		actions={[ {
			text: intl.formatMessage({ id: "TEMPLATES_TITLE" }),
			onClick: () => openAgreementTemplatesDialog(true),
		}, {
			text: intl.formatMessage({ id: "CHANGE_ORDER" }),
			onClick: () => openAgreementOrderDialog(true),
			disabled: fields.length < 2,
		}, {
			render: () => (charterType && (
				<AddAgreementButton
					charterType={charterType}
					charterSubtype={charterSubtype}
					agreementTypes={agreementTypes}
					customAgreementTypes={customAgreementTypes}
					append={(type: AgreementTypeEnum) => onAppend(type)}
					isParentContract={isParentContract}
					isChildContract={isChildContract}
				/>
			)),
		} ]}
	>
		<FormArrayContainer>
			{fields.map((agreement, index) => (
				<FormArrayRow
					color={getColor(index, agreement)}
					key={agreement.generatedId}
					onDuplicate={() => onDuplicate(index)}
					onDelete={() => onRemove(index)}
					onCustomActions={[ {
						render: () => {
							const type = agreements[index]?.type;
							const schema = type ? AgreementSchema[type] : undefined;
							return !schema?.isNotDeclarable && (
								<Box key="invoiceGroupType" sx={{ minWidth: 200 }} onClick={e => e.stopPropagation()}>
									<InvoiceGroupTypeSelect
										placeholder={intl.formatMessage({ id: "INVOICE_GROUP_TYPE" })}
										charterType={charterType}
										value={agreements[index]?.invoiceGroupType}
										includeParentContractExclusive={isParentContract}
										onChange={option => {
											updateAgreement(index, {
												...agreements[index],
												invoiceGroupType: option ?? undefined,
											});
										}} />
								</Box>
							);
						},
					} ]}
					title={<Box sx={{ display: "flex", alignItems: "center" }}>
						{agreement.isDefault && (<Tooltip sx={{ marginRight: 1 }} title={intl.formatMessage({ id: "DEFAULT_AGREEMENT_MISSING_DATA" })}><InfoOutlined color="error" fontSize="small" /></Tooltip>)}
						{getTitle(index)}
					</Box>}
					subtitle={getSubtitle(index)}
				>
					<AgreementForm
						customAgreementTypes={customAgreementTypes}
						updateAgreement={updateAgreement}
						agreementTypes={agreementTypes}
						session={session}
						defaultAgreement={createDefaultAgreement()}
						index={index}
						currency={currency} />
				</FormArrayRow>
			))}
		</FormArrayContainer>

		{charterType && (
			<AgreementTemplatesDialog
				open={agreementTemplatesDialogOpen}
				onClose={() => openAgreementTemplatesDialog(false)}
				onAddAgreements={(agreements) => {
					const currentAgreements = agreements.map((x) => stripTemplateIds(convertAgreementToForm(x)));
					currentAgreements.forEach(x => append(x));
				}}
				onOverwriteAgreements={(agreements) => {
					const currentAgreements = agreements.map((x) => stripTemplateIds(convertAgreementToForm(x)));
					replace(currentAgreements);
				}}
				compatibleCharterType={charterType}
				compatibleCharterSubtype={charterSubtype}
			/>
		)}

		<UpdateAgreementOrderDialog
			open={agreementOrderDialogOpen}
			onClose={() => openAgreementOrderDialog(false)}
			refetch={(newList) => newList.forEach((item, index) => update(index, item))}
			agreements={agreements} />
	</Card>);
};

export default AgreementCreateEdit;
